import { useEffect, useState } from "react";
import { Bitcoin, Copy, Check, HeartHandshake } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Donate() {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState({ total_btc: 0, count: 0, items: [] });
  const [txId, setTxId] = useState("");
  const [amount, setAmount] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [c, s] = await Promise.all([api.get("/payment/config"), api.get("/donations")]);
    setCfg(c.data);
    setStats(s.data);
  };

  useEffect(() => {
    load();
  }, []);

  const copyAddr = () => {
    if (!cfg) return;
    navigator.clipboard.writeText(cfg.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const record = async () => {
    if (!txId || !amount) return;
    setLoading(true);
    try {
      await api.post("/donations", { tx_id: txId, amount_btc: parseFloat(amount) });
      toast.success("Thanks for the donation.");
      setTxId("");
      setAmount("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const qrUrl = cfg
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=bitcoin:${cfg.address}`
    : null;

  return (
    <main className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex items-center gap-3 mb-4">
        <HeartHandshake className="w-6 h-6 text-[#F7931A]" />
        <h1 className="font-display font-black text-4xl lg:text-5xl uppercase tracking-tighter">
          Fund Bitbook.
        </h1>
      </div>
      <p className="text-[#0A0A0A]/70 max-w-xl">
        Bitbook runs on donations, not ads. Every sat goes toward keeping this network open.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-black">
        <div className="p-8 bg-[#0A0A0A] text-[#FDFBF7]">
          <div className="font-mono text-xs uppercase tracking-widest text-[#F7931A] flex items-center gap-2">
            <Bitcoin className="w-4 h-4" /> Wallet
          </div>
          <div className="mt-6 flex justify-center">
            {qrUrl && <img src={qrUrl} alt="Bitcoin QR" data-testid="donate-qr" className="border-4 border-white bg-white" />}
          </div>
          <div className="mt-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">
              Address
            </div>
            <div data-testid="donate-address" className="font-mono text-xs break-all bg-white/10 p-3 border border-white/20 mt-2">
              {cfg?.address || "..."}
            </div>
            <button
              onClick={copyAddr}
              data-testid="btn-copy-donate-address"
              className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest border border-white/40 px-3 py-2 hover:bg-white hover:text-black transition-colors"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? "Copied" : "Copy address"}
            </button>
          </div>
        </div>

        <div className="p-8 bg-white">
          <div className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
            Total raised
          </div>
          <div className="font-display font-black text-5xl tracking-tighter mt-2">
            {stats.total_btc.toFixed(6)} <span className="text-[#F7931A]">BTC</span>
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60 mt-1">
            {stats.count} donations
          </div>

          <div className="mt-8 pt-6 border-t-2 border-black">
            <div className="font-mono text-xs uppercase tracking-widest">Log your donation</div>
            <input
              value={txId}
              onChange={(e) => setTxId(e.target.value)}
              placeholder="Transaction ID"
              data-testid="donate-tx"
              className="mt-3 w-full px-3 py-2 bg-[#FDFBF7] border-2 border-black font-mono text-xs"
            />
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount in BTC"
              type="number"
              step="0.00000001"
              data-testid="donate-amount"
              className="mt-3 w-full px-3 py-2 bg-[#FDFBF7] border-2 border-black font-mono text-sm"
            />
            <button
              onClick={record}
              disabled={loading || !txId || !amount}
              data-testid="btn-donate-submit"
              className="mt-4 w-full py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest text-sm btn-brutal disabled:opacity-50"
            >
              {loading ? "..." : "Log donation"}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60 mb-3">
          Recent supporters
        </div>
        {stats.items.length === 0 ? (
          <div className="text-sm text-[#0A0A0A]/60 font-mono">No donations recorded yet.</div>
        ) : (
          <div className="border-t-2 border-black">
            {stats.items.map((d) => (
              <div
                key={d.id}
                data-testid={`donation-${d.id}`}
                className="flex items-center justify-between py-3 border-b border-black/20"
              >
                <div className="font-display font-bold">{d.user_name}</div>
                <div className="font-mono text-sm text-[#F7931A] font-bold">
                  {d.amount_btc} BTC
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
